const MAX_DIMENSION = 1600;
const JPEG_QUALITY = 0.85;

export interface CompressedImage {
  mimeType: string;        // "image/jpeg" or "application/pdf"
  dataBase64: string;
  blob: Blob;
  kind: "image" | "pdf";
  name: string;            // for display on thumbnails (especially PDFs)
}

/**
 * Process either an image (compressed + EXIF-corrected) or a PDF (raw pass-
 * through). Gemini accepts both as inlineData; the backend keys off the
 * mimeType to decide the Drive file extension.
 */
export async function processFile(file: File): Promise<CompressedImage> {
  const isPdf =
    file.type === "application/pdf" ||
    file.name.toLowerCase().endsWith(".pdf");
  if (isPdf) return passthroughPdf(file);
  return compressImage(file);
}

async function passthroughPdf(file: File): Promise<CompressedImage> {
  const dataBase64 = await blobToBase64(file);
  return {
    mimeType: "application/pdf",
    dataBase64,
    blob: file,
    kind: "pdf",
    name: file.name,
  };
}

export async function compressImage(file: File): Promise<CompressedImage> {
  // imageOrientation: "from-image" makes the browser apply the JPEG EXIF
  // orientation tag when rasterising. iOS Safari photos are stored sideways
  // with an EXIF rotation flag — without this, Gemini sees a 90° rotated
  // receipt and struggles to read anything.
  const bitmap = await createImageBitmap(file, { imageOrientation: "from-image" });
  const scale = Math.min(1, MAX_DIMENSION / Math.max(bitmap.width, bitmap.height));
  const w = Math.round(bitmap.width * scale);
  const h = Math.round(bitmap.height * scale);

  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas 2d context unavailable");
  ctx.drawImage(bitmap, 0, 0, w, h);

  const blob: Blob = await new Promise((resolve, reject) =>
    canvas.toBlob((b) => b ? resolve(b) : reject(new Error("toBlob failed")), "image/jpeg", JPEG_QUALITY)
  );
  const dataBase64 = await blobToBase64(blob);
  return {
    mimeType: "image/jpeg",
    dataBase64,
    blob,
    kind: "image",
    name: file.name,
  };
}

function blobToBase64(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const idx = result.indexOf(",");
      resolve(idx >= 0 ? result.slice(idx + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}
